import { useEffect, useState } from 'react';
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Typography,
} from '@mui/material';

function value(value) {
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

function ProtectionRow({ label, id, quantity, status }) {
  return (
    <Typography variant="body2">
      {label}: {value(status)} · quantité {value(quantity)} · id {value(id)}
    </Typography>
  );
}

export default function BlofinLiveReconcileDialog({
  open, intent, busy, error, onClose, onConfirm,
  exchangeName = 'BloFin',
  createsProtectionOrders = false,
  requiresAcknowledgement = false,
}) {
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open, intent?.intentId]);

  if (!intent) return null;
  const isBitunix = createsProtectionOrders || requiresAcknowledgement;
  const canConfirm = !busy && (!requiresAcknowledgement || acknowledged);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isBitunix ? `Réconcilier les protections ${exchangeName}` : 'Reconcilier le SL distant'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            {isBitunix
              ? 'Cette action peut créer des ordres TP réels sur Bitunix pour les protections manquantes. Elle adopte d’abord les protections existantes et ne doit jamais créer de doublon.'
              : 'Cette action ne crée aucun ordre MARKET et ne crée aucun SL. Elle lit la position et les TPSL distants, puis adopte le SL existant uniquement si son clientOrderId, son instrument, son sens, son prix et sa couverture correspondent exactement.'}
          </Alert>
          <Stack spacing={0.5}>
            <Typography variant="body2">Intention : {intent.intentId}</Typography>
            <Typography variant="body2">Instrument : {intent.instrumentId}</Typography>
            <Typography variant="body2">Statut : {intent.status}</Typography>
            <Typography variant="body2">Protection : {intent.slState} / {intent.tpState}</Typography>
            <Typography variant="body2">Reste : {value(intent.remainingContracts)}</Typography>
            <ProtectionRow label="SL" id={intent.exchangeSlOrderId} quantity={intent.filledContracts}
              status={intent.slState} />
            <ProtectionRow label="TP1" id={intent.exchangeTp1OrderId} quantity={intent.tp1Contracts}
              status={intent.tp1FilledContracts != null ? 'PARTIALLY_OR_FULLY_FILLED' : 'ACTIVE_OR_PENDING'} />
            <ProtectionRow label="TP2" id={intent.exchangeTp2OrderId} quantity={intent.tp2Contracts}
              status={intent.tp2FilledContracts != null ? 'PARTIALLY_OR_FULLY_FILLED' : 'ACTIVE_OR_PENDING'} />
            <ProtectionRow label="TP3" id={intent.exchangeTp3OrderId} quantity={intent.tp3Contracts}
              status={intent.tp3FilledContracts != null ? 'PARTIALLY_OR_FULLY_FILLED' : 'ACTIVE_OR_PENDING'} />
            <Typography variant="body2" color="error">
              Dernière erreur : {intent.lastError || '—'}
            </Typography>
          </Stack>
          <Alert severity="info">
            La protection finale ne sera déclarée ACTIVE qu’après vérification réelle du SL et des
            trois TP sur la position exacte.
          </Alert>
          {requiresAcknowledgement && (
            <FormControlLabel
              control={<Checkbox checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)} />}
              label="Je comprends que cette réconciliation peut créer des TP réels sur Bitunix."
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Annuler</Button>
        <Button color="warning" variant="contained" disabled={!canConfirm} onClick={onConfirm}>
          {busy ? <CircularProgress size={18} /> : (isBitunix ? 'Réconcilier les protections' : 'Reconcilier le SL')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
